import { FC, Fragment, ReactNode } from 'react';

import { ImagesApp } from '../../constants/images';
import { useCartContext } from '../../hooks/contexts/cart.context';
import { useComboProgressContext } from '../../hooks/contexts/comboProgress.context';
import { useEventContext } from '../../hooks/contexts/event.context';
import { useI18nContext } from '../../hooks/contexts/i18n.context';
import { useUiContext } from '../../hooks/contexts/ui.context';
import { ComboTicketsSelected } from '../ComboTicketsList';
import Toast from '../Toast';
import * as Styles from './layout.styles';

type ComboLayoutProps = {
  selectedCombo?: number | null;
  selectedSession?: string[];
  selectedTickets?: ComboTicketsSelected[];
  curComponent: 'combo' | 'sessions' | 'tickets';
  children : ReactNode;
  btnIsDisable? : boolean;
};

const ComboLayout: FC<ComboLayoutProps> = ({
  children,
  selectedCombo,
  selectedSession,
  selectedTickets,
  curComponent,
  btnIsDisable = false,
}) => {
  const {
    breadcrumbs,
    comboData,
    handleUpdateComboBreadcrumbs,
    selectCombo,
    selectSessions,
    selectTickets,
    openToast,
    handleCloseToast,
  } = useComboProgressContext();
  const { isMobile } = useUiContext();
  const { translate } = useI18nContext();
  const { comboList } = useEventContext();
  const { combosInCart } = useCartContext();

  const lastStep = breadcrumbs.length - 1;
  const currentStep = breadcrumbs.find((breadcrumb) => breadcrumb.active);
  const isFirstStep = breadcrumbs[0].active;
  const isLastStep = breadcrumbs[lastStep].active;

  const handleMinMaxDescription = () => {
    if (comboData.id) {
      const selectedCombo = comboList.find((combo) => combo.id === comboData.id) || comboList[0];
      return {
        min : String(selectedCombo.sessionMin),
        max : String(selectedCombo.sessionMax)
      };
    }
  };

  const handleNextStep = () => {
    if (curComponent === 'combo' && selectedCombo) selectCombo(Number(selectedCombo));
    if (curComponent === 'sessions' && selectedSession) selectSessions(selectedSession);
    if (curComponent === 'tickets' && selectedTickets) selectTickets(selectedTickets);
  };

  return (
    <Fragment>
      {combosInCart.length > 0 && (
        <Styles.ListOfCreatedCombos>
          {openToast && (
            <Toast
              title={translate(['COMBO', 'TOAST', 'TITLE'])}
              icon={ImagesApp.svg.check}
              handleClose={handleCloseToast}
            >
              {translate(['COMBO', 'TOAST', 'DESCRIPTION'])}
            </Toast>
          )}
          {combosInCart.map((combo) => (
            <Styles.CreatedComboCard key={combo.id}>
              <h2>{combo.name}</h2>
              <img src={ImagesApp.svg.check} alt='check' />
            </Styles.CreatedComboCard>
          ))}
        </Styles.ListOfCreatedCombos>
      )}
      <Styles.ComboLayout>
        <header>
          <Styles.ComboBreadcrumbContainer>
            {breadcrumbs.map((breadcrumb, index) => (
              <Styles.ComboBreadcrumbStep key={index}>
                <Styles.ComboBreadcrumbStepText $active={breadcrumb.active}>
                  { !isMobile || breadcrumb.active ? translate(breadcrumb.name) : (index + 1)}
                </Styles.ComboBreadcrumbStepText>
                {index !== lastStep && <Styles.ComboBreadcrumbDotSeparator>•</Styles.ComboBreadcrumbDotSeparator>}
              </Styles.ComboBreadcrumbStep>
            ))}
          </Styles.ComboBreadcrumbContainer>
          <Styles.ComboArticleHeader>
            <h2>{currentStep && translate(currentStep?.title)}</h2>
            <p
              dangerouslySetInnerHTML={{ __html : currentStep ? (
                translate(currentStep?.description, handleMinMaxDescription())
              ) : '' }}
            />
          </Styles.ComboArticleHeader>
        </header>
        <Fragment>
          {children}
        </Fragment>
        <Styles.ComboListBtnContainer $isFirstStep={isFirstStep}>
          {!isFirstStep && (
            <Styles.ComboListButton
              $isBackBtn={true}
              onClick={ () => handleUpdateComboBreadcrumbs(-1) }
            >
              {translate(['COMBO', 'COMBO_BTN_STEPS', 'BACK_BTN'])}
            </Styles.ComboListButton>
          )}
          <Styles.ComboListButton
            $isBackBtn={false}
            disabled={btnIsDisable}
            onClick={ () => handleNextStep() }
          >
            {isLastStep ? (
              translate(['COMBO', 'COMBO_BTN_STEPS', 'CONFIRM_BTN'])
            ) : (
              translate(['COMBO', 'COMBO_BTN_STEPS', 'NEXT_BTN'])
            )}
          </Styles.ComboListButton>
        </Styles.ComboListBtnContainer>
      </Styles.ComboLayout>
    </Fragment>
  );
};

export default ComboLayout;
